/**
 * Icon + filename chip for a non-media attachment. `AttachmentTray` renders it
 * while composing and `RichContent` renders it once sent, so a PDF you attach
 * looks like the PDF you see afterward — same icon, same shape.
 */
export function FileChip({
  name,
  meta,
  href,
  action,
}: {
  /** The filename, already entity/URL-decoded. Truncated with an ellipsis if it overflows. */
  name: string;
  /** Extension/size, e.g. "PDF · 149 KB" — shown next to the name when either is known. */
  meta?: string;
  /** The uploaded file's URL. Present once sent (RichContent); omitted while composing (AttachmentTray). */
  href?: string;
  /** Trailing slot — a remove button while composing, a spinner while uploading. */
  action?: React.ReactNode;
}) {
  const classes = `flex items-center gap-2 pl-2 pr-1 py-1.5 bg-surface-2/50 rounded-lg border border-border${
    href ? " hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors" : ""
  }`;

  const inner = (
    <>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-muted-foreground shrink-0"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
      <span className="flex-1 min-w-0 text-xs text-foreground" title={name}>
        <span className="block truncate">{name}</span>
        {meta && <span className="block truncate text-subtle-foreground">{meta}</span>}
      </span>
      {action}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={classes}
        aria-label={`Åbn ${name}`}
      >
        {inner}
      </a>
    );
  }

  return <div className={classes}>{inner}</div>;
}
